import { Circle, Tooltip } from '@chakra-ui/react';
import gql from 'graphql-tag';
import { ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import { AcPowerIcon } from '~components/ui/icons/AcPowerIcon';
import { Battery50PercentIcon } from '~components/ui/icons/Battery50PercentIcon';
import { Battery50PercentManagedIcon } from '~components/ui/icons/Battery50PercentManagedIcon';
import { Battery80PercentIcon } from '~components/ui/icons/Battery80PercentIcon';
import { Battery80PercentManagedIcon } from '~components/ui/icons/Battery80PercentManagedIcon';
import { Battery100PercentIcon } from '~components/ui/icons/Battery100PercentIcon';
import { Battery100PercentManagedIcon } from '~components/ui/icons/Battery100PercentManagedIcon';
import { BatteryExhaustedIcon } from '~components/ui/icons/BatteryExhaustedIcon';
import { CriticalBatteryIcon } from '~components/ui/icons/CriticalBatteryIcon';
import { CriticalBatteryManagedIcon } from '~components/ui/icons/CriticalBatteryManagedIcon';
import { IconProps } from '~components/ui/icons/types';
import { WarningBatteryIcon } from '~components/ui/icons/WarningBatteryIcon';
import { WarningBatteryManagedIcon } from '~components/ui/icons/WarningBatteryManagedIcon';
import { BatteryBadge_DisplayFragment } from './__generated__/BatteryBadge.graphql';

type IconComponent = ComponentType<IconProps>;

interface Props {
  display: BatteryBadge_DisplayFragment;
}

function getBatteryIcon(
  level: number,
  powerSource: string,
  powerManagement: string,
): { icon: IconComponent; bg: string; color: string } {
  const isAC = powerSource === 'AC';
  const isManaged = powerManagement === 'ON';

  if (isAC) {
    return { icon: AcPowerIcon, bg: 'blue.50', color: 'blue.400' };
  }

  if (level <= 5) {
    return {
      icon: isManaged ? CriticalBatteryManagedIcon : CriticalBatteryIcon,
      bg: 'red.50',
      color: 'red.400',
    };
  }

  if (level <= 15) {
    return {
      icon: isManaged ? WarningBatteryManagedIcon : WarningBatteryIcon,
      bg: 'orange.50',
      color: 'orange.400',
    };
  }

  if (level <= 50) {
    return {
      icon: isManaged ? Battery50PercentManagedIcon : Battery50PercentIcon,
      bg: 'green.50',
      color: 'green.400',
    };
  }

  if (level <= 80) {
    return {
      icon: isManaged ? Battery80PercentManagedIcon : Battery80PercentIcon,
      bg: 'green.50',
      color: 'green.400',
    };
  }

  return {
    icon: isManaged ? Battery100PercentManagedIcon : Battery100PercentIcon,
    bg: 'green.50',
    color: 'green.400',
  };
}

function getBatteryTooltip(
  level: number,
  powerSource: string,
  powerManagement: string,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  if (powerSource === 'AC') return t('batteryTooltipAC');
  if (powerManagement === 'ON') return t('batteryTooltipBatteryManaged', { level });
  return t('batteryTooltipBattery', { level });
}

export function BatteryBadge({ display }: Props) {
  const { t } = useTranslation();
  const battery = display.battery?.reported;

  if (!battery) return null;

  // Battery physically absent but running on battery = exhausted
  if (!battery.present && battery.powerSource === 'BATTERY') {
    return (
      <Tooltip label={t('batteryTooltipExhausted')} hasArrow>
        <Circle padding={3} bg="red.50" color="red.400">
          <BatteryExhaustedIcon width={6} height={6} />
        </Circle>
      </Tooltip>
    );
  }

  if (!battery.present) return null;

  const { icon, bg, color } = getBatteryIcon(
    battery.level,
    battery.powerSource,
    battery.powerManagement,
  );

  const tooltip = getBatteryTooltip(battery.level, battery.powerSource, battery.powerManagement, t);
  const Icon = icon;
  const padding = 3;
  const size = 6;

  return (
    <Tooltip label={tooltip} hasArrow>
      <Circle padding={padding} bg={bg} color={color}>
        <Icon width={size} height={size} />
      </Circle>
    </Tooltip>
  );
}

BatteryBadge.graphql = {
  fragments: {
    BatteryBadge_display: gql`
      fragment BatteryBadge_display on Display {
        id
        battery {
          reportedAt
          reported {
            present
            level
            powerSource
            powerManagement
          }
        }
      }
    `,
  },
};
